import { useLayoutEffect, type ReactNode } from 'react';
import { ShieldAlert } from 'lucide-react';
import { currentRuntime, isRuntimeTenantResolved } from '../../config/runtime';

export default function RuntimeTenantGate({ children }: { children: ReactNode }) {
  useLayoutEffect(() => {
    const bootstrapShell = document.getElementById('bootstrap-shell');
    if (!bootstrapShell) return;

    if (!isRuntimeTenantResolved(currentRuntime)) {
      // Non-warrior path: remove immediately, no React logo to show
      bootstrapShell.remove();
      return;
    }

    // Immediately remove bootstrap shell if the current path is a public route (such as /beta)
    // so it never intercepts clicks or touch events on public forms.
    const pathname = String(window.location.pathname || '/').replace(/\/+$/, '') || '/';
    if (pathname === '/beta' || pathname === '/login' || pathname === '/signup') {
      bootstrapShell.remove();
      return;
    }

    // Crossfade the bootstrap shell out immediately so the React PulseShieldLoader
    // can take single visual ownership. We apply an inline opacity transition and
    // remove the element after it completes — avoiding a double-logo flash.
    bootstrapShell.style.transition = 'opacity 200ms ease-out';
    bootstrapShell.style.opacity = '0';
    bootstrapShell.setAttribute('aria-hidden', 'true');
    bootstrapShell.addEventListener(
      'transitionend',
      () => bootstrapShell.remove(),
      { once: true },
    );
  }, []);

  if (isRuntimeTenantResolved(currentRuntime)) {
    return <>{children}</>;
  }

  return (
    <main
      data-testid="runtime-tenant-unresolved"
      className="fixed inset-0 z-[10000] flex min-h-screen items-center justify-center bg-[#020817] p-6 text-slate-200"
    >
      <div className="w-full max-w-md rounded-2xl border border-slate-700/60 bg-slate-900/85 p-7 text-center shadow-2xl">
        <ShieldAlert className="mx-auto h-10 w-10 text-sky-300" aria-hidden="true" />
        <h1 className="mt-5 text-xl font-semibold text-white">We couldn’t identify this workspace.</h1>
        <p className="mt-3 text-sm leading-6 text-slate-400">
          Check the address and try again. No tenant workspace has been loaded.
        </p>
      </div>
    </main>
  );
}
